<template>
    <auth-layout>
        <div class="auth-action-wrap">
            <skeleton-loader v-if="state === 'loading'" type="auth-action" />

            <template v-else-if="state === 'reset'">
                <div class="status-icon status-icon--accent">
                    <v-icon size="30" color="accent">mdi-lock-reset</v-icon>
                </div>
                <h1 class="status-title">{{ $t('auth_action.title_reset') }}</h1>
                <p class="status-subtitle">{{ $t('auth_action.body_reset') }}</p>

                <v-form v-model="valid" class="text-left" @submit.prevent="submitReset">
                    <kimsa-password-field
                        :label="$t('auth_action.new_password')"
                        :placeholder="$t('auth_action.new_password')"
                        :rules="passwordRules"
                        @change="newPassword = $event"
                        @enter="submitReset"
                    />
                    <v-btn
                        color="primary" depressed block large type="submit"
                        class="black--text text-none font-weight-bold mt-5"
                        :loading="processing" :disabled="newPassword.length < 6"
                    >{{ $t('auth_action.save') }}</v-btn>
                </v-form>
            </template>

            <template v-else>
                <div class="status-icon" :class="`status-icon--${statusView.tone}`">
                    <v-icon size="30" :color="statusView.tone">{{ statusView.icon }}</v-icon>
                </div>
                <h1 class="status-title">{{ statusView.title }}</h1>
                <p class="status-subtitle">{{ statusView.body }}</p>
                <v-btn
                    color="primary" depressed block large @click="continueToApp"
                    class="black--text text-none font-weight-bold"
                >{{ $t('auth_action.continue') }}</v-btn>
            </template>
        </div>
    </auth-layout>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/auth'

import AuthLayout from '@/components/entrance/AuthLayout'
import KimsaPasswordField from '@/components/common/inputs/KimsaPasswordField'
import SkeletonLoader from '@/components/common/SkeletonLoader'
import { ANDROID_APP_SCHEME, IOS_APP_SCHEME, STUDENTS_APP_SCHEME, isIosDevice } from '@/utils/Platform'

export default {
    components: {
        AuthLayout,
        KimsaPasswordField,
        SkeletonLoader,
    },
    data(vm = this) {
        return {
            state: 'loading',
            mode: null,
            oobCode: null,
            appHint: null,
            email: '',
            newPassword: '',
            valid: false,
            processing: false,
            errorMsg: '',
            passwordRules: [
                v => !!v || vm.$t('rules.required'),
                v => (v && v.length >= 6) || vm.$t('rules.min_length', { num: '6' }),
            ],
        }
    },
    computed: {
        statusView(vm = this) {
            const views = {
                verified: { tone: 'success', icon: 'mdi-check-circle', title: vm.$t('auth_action.title_verified'), body: vm.$t('auth_action.body_verified') },
                recovered: { tone: 'success', icon: 'mdi-email-check', title: vm.$t('auth_action.title_recovered'), body: vm.$t('auth_action.body_recovered') },
                resetDone: { tone: 'success', icon: 'mdi-check-circle', title: vm.$t('auth_action.title_reset_done'), body: vm.$t('auth_action.body_reset_done') },
                error: { tone: 'error', icon: 'mdi-alert-circle', title: vm.$t('auth_action.title_error'), body: vm.errorMsg },
            }
            return views[vm.state] || views.error
        },
    },
    async created() {
        const params = new URLSearchParams(window.location.search)
        this.mode = params.get('mode')
        this.oobCode = params.get('oobCode')
        const lang = params.get('lang')
        const continueUrl = params.get('continueUrl')
        try {
            if (continueUrl) this.appHint = new URL(continueUrl).searchParams.get('app')
        } catch { /* ignore */ }
        if (!this.appHint) this.appHint = params.get('app')
        if (lang === 'en' || lang === 'es') this.$i18n.locale = lang
        try { window.history.replaceState({}, '', window.location.pathname + window.location.hash) } catch { /* ignore */ }
        await this.handle()
    },
    methods: {
        async handle() {
            if (!this.oobCode || !this.mode) {
                this.state = 'error'
                this.errorMsg = this.$t('auth_action.invalid')
                return
            }
            try {
                if (this.mode === 'verifyEmail') {
                    await firebase.auth().applyActionCode(this.oobCode)
                    this.state = 'verified'
                } else if (this.mode === 'recoverEmail') {
                    await firebase.auth().applyActionCode(this.oobCode)
                    this.state = 'recovered'
                } else if (this.mode === 'resetPassword') {
                    this.email = await firebase.auth().verifyPasswordResetCode(this.oobCode)
                    this.state = 'reset'
                } else {
                    this.state = 'error'
                    this.errorMsg = this.$t('auth_action.invalid')
                }
            } catch (err) {
                console.log('auth action error', err)
                this.state = 'error'
                this.errorMsg = this.$t('auth_action.expired')
            }
        },
        async submitReset() {
            if (this.newPassword.length < 6) return
            this.processing = true
            try {
                await firebase.auth().confirmPasswordReset(this.oobCode, this.newPassword)
                this.state = 'resetDone'
            } catch (err) {
                console.log('confirmPasswordReset error', err)
                this.state = 'error'
                this.errorMsg = this.$t('auth_action.expired')
            } finally {
                this.processing = false
            }
        },
        continueToApp() {
            const scheme = this.appHint === 'students' ? STUDENTS_APP_SCHEME : (isIosDevice() ? IOS_APP_SCHEME : ANDROID_APP_SCHEME)
            const timer = setTimeout(() => {
                if (!document.hidden) window.location.href = '/'
            }, 1500)
            document.addEventListener('visibilitychange', () => clearTimeout(timer), { once: true })
            window.location.href = `${scheme}://auth?from=email`
        },
    },
}
</script>

<style scoped>
.auth-action-wrap {
    width: 100%;
    max-width: 380px;
    text-align: center;
}

.status-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
}

.status-icon--accent {
    background: rgba(51, 153, 153, 0.12);
}

.status-icon--success {
    background: rgba(0, 153, 102, 0.12);
}

.status-icon--error {
    background: rgba(232, 92, 92, 0.12);
}

.status-title {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin-bottom: 10px;
}

.status-subtitle {
    font-size: 14px;
    color: var(--v-txtSecondary-base);
    line-height: 1.6;
    margin-bottom: 24px;
}
</style>
